import { Amplify } from "aws-amplify";
import i18n from "i18next";
import * as Sentry from "@sentry/browser";
import { store, persistor } from "../store";
import eventBus from "../eventBus";
import { userSetPasswordUpdated, getUniqueSessionId } from "../redux/actions/AuthActions";
import { SIGNOUT_USER } from "../redux/actions/Common";
import { getMonthFromDate, getUuid } from "./Number";
import { isMobile, getOsDetails } from "./Location";
import { ApiClient } from "../api/ApiClient";
import { ApiError, apiErrorCodes } from "../api/ApiResponse";
import Cookies from "js-cookie";
import {
  siteConfigUserPoolIdSelector,
  siteConfigClientIdSelector,
  accountEndTsSelector,
  accountCurrentTsSelector,
  accountRemainingDaysSelector,
  isInOptinTrialSelector,
  blackRemainingOptinTrialDaysSelector
} from "../redux/reducers/AuthReducer";
import { clearCustodianDetailsCache } from "../redux/reducers/CustodianDetailsReducer";
import {
  userSelector,
  userPreferencesSelector,
  removePortfolioSessionUserId,
  removeLastUsedPortfolioUserId
} from "../redux/reducers/Common";
import { detect } from "detect-browser";
import {
  getQueryParams,
  VIEW_ONLY_USER_NAME,
  Routes,
  isAppInViewMode,
  VIEW_ONLY_TOKEN_KEY,
  isAppInWhiteLabelMode,
  getRecaptchaToken,
  getRecaptchaTokenType,
  getRecaptchaV3Token,
  getSiteWhiteLabelConfigId,
  getSWConsts,
  isV2WidgetLoaded,
  setUserNameForViewMode,
  VIEW_ONLY_PASSCODE_KEY,
  resetRecaptchaV2,
  USER_LAMBDA_VALIDATION_EXCEPTION,
  cognitoErrorCodes,
  recaptchaTypes,
  parentActions
} from "./Common";
import Auth, { verifySigninPassword, formatPhoneNumber } from "./AmplifyAuth";
import { installAwsWafTokenForCognito } from "./AwsWaf";
import { toUserTimezoneDate } from "./Timezone";

export const getSubscriptionDateString = (state, accountEndTs) => {
  const date = toUserTimezoneDate(state, accountEndTs * 1000);
  return `${date.getDate()} ${getMonthFromDate(date)}`;
};

export const getSubscriptionEndDateString = (state, accountEndTs) => {
  const date = toUserTimezoneDate(state, accountEndTs * 1000);
  return `${date.getDate()} ${getMonthFromDate(date)} ${date.getFullYear()}`;
};

export const checkIfBannerPreferenceShouldBeSetNull = state => {
  const user = userSelector(state);
  const isInOptinTrial = isInOptinTrialSelector(state);
  const accountEndTs = accountEndTsSelector(state);
  const accountCurrentTs = accountCurrentTsSelector(state);
  const remainingDays = accountRemainingDaysSelector(state);
  const blackRemainingOptinTrialDays = blackRemainingOptinTrialDaysSelector(state);
  const currentDate = getSubscriptionEndDateString(state, accountCurrentTs);
  const oneLessEndTs = getSubscriptionEndDateString(state, accountEndTs - 86400);
  const blackEndTs = getSubscriptionEndDateString(state, (user?.tsExtendedTrialStart || 0) + 86400 * 13);
  const userPreferences = userPreferencesSelector(state);
  const preferenceValue = isInOptinTrial
    ? (blackRemainingOptinTrialDays <= 10 && blackRemainingOptinTrialDays > 5 && 10) ||
      (blackRemainingOptinTrialDays <= 5 && blackRemainingOptinTrialDays > 2 && 5) ||
      (blackRemainingOptinTrialDays >= 1 && 2) ||
      (currentDate === blackEndTs && "tomorrow") ||
      blackRemainingOptinTrialDays
    : (remainingDays <= 10 && remainingDays > 5 && 10) ||
      (remainingDays <= 5 && remainingDays > 2 && 5) ||
      (remainingDays >= 1 && 2) ||
      (currentDate === oneLessEndTs && "tomorrow") ||
      remainingDays;
  if (userPreferences.bannerPreference !== null && userPreferences.bannerPreference !== preferenceValue) {
    return true;
  }
  return false;
};

export const getCurrentUser = async (onSuccess, onError) => {
  await Auth.currentAuthenticatedUser({
    bypassCache: false
  })
    .then(user => onSuccess(user))
    .catch(err => onError(err));
};

export const isAuthenticatedByThirdPartyProvider = async () => {
  const result = await Auth.currentAuthenticatedUser({
    bypassCache: false
  })
    .then(user => {
      // DO NOT CHANGE THIS
      // Starts with "google" for users logged in before we introduced create password change for google logged in users
      // Google logged in accounts after will not have this prefix
      return user.username.toLowerCase().startsWith("google");
    })
    .catch(() => {
      return true;
    });
  return result;
};

export const isUserSignedIn = async () => {
  const result = await Auth.currentSession()
    .then(() => {
      return true;
    })
    .catch(() => {
      return false;
    });
  return result;
};

export const signOut = async (redirectToLoginScreen = true, locationSearch = null, onComplete = null) => {
  window.isSigningOut = true;
  const cleanupBeforeSignout = () => {
    Cookies.set("kuberaAppLastAccessSecure", null, {
      sameSite: "Strict",
      secure: true,
      expires: 365,
      domain: ".kubera.com"
    });
    localStorage.setItem("INITIAL_GREETING_SHOWN_KEY", false);
    localStorage.removeItem("remember_me");
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
        localStorage.removeItem(key);
      }
    });
    sessionStorage.removeItem("selectedPortfolioIdForConnectivityCenter");
    clearCustodianDetailsCache();

    removePortfolioSessionUserId();
    removeLastUsedPortfolioUserId();
    window.name = null;

    navigator.serviceWorker?.controller?.postMessage({
      type: SIGNOUT_USER,
      SW_CONSTS: getSWConsts()
    });

    persistor.pause();
    return persistor
      .flush()
      .then(() => {
        return persistor.purge();
      })
      .catch(error => {
        console.log("Store purge error", error);
      });
  };

  const doManualSignoutRedirect = () => {
    if (redirectToLoginScreen === true) {
      var url = Routes.SIGNOUT;
      if (!locationSearch === false) {
        url += "?" + locationSearch;
      }
      window.location.href = url;
    }
  };

  const authSignout = async () => {
    await cleanupBeforeSignout();
    return Auth.signOut().then(() => {
      doManualSignoutRedirect();
    });
  };

  await ApiClient.signOut(getUuid())
    .then(() => authSignout())
    .catch(async error => {
      console.log("Signout error", error);
      await cleanupBeforeSignout();
      await Auth.signOut();
      doManualSignoutRedirect();
    });

  if (onComplete) {
    onComplete();
  }
};

export const isEmailValid = email => {
  const emailRegex = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;
  return emailRegex.test(email);
};

export const signInWithGoogle = async () => {
  Auth.federatedSignIn({ provider: "Google" });
};

// Wrong password, or no account with this email on this site. Cognito returns
// one or the other depending on the pool's user-existence-errors setting, so
// the UI treats both the same and never says which one it was.
export const isInvalidCredentialsError = error =>
  error?.code === cognitoErrorCodes.USER_NOT_FOUND ||
  (error?.code === cognitoErrorCodes.NOT_AUTHORIZED && /incorrect username or password/i.test(error.message || ""));

// Lets the backend email the account owner (failed-attempt alert, or a link
// to the right sign-in page). Fire and forget: the response is the same
// whether or not an account exists, and a failure here must not affect the flow.
const notifyAuthFailure = (send, recaptchaAction, email, clientInfo) => {
  getRecaptchaV3Token(recaptchaAction)
    .then(recaptchaToken =>
      send(getUuid(), {
        email,
        recaptchaToken,
        type: recaptchaTypes.V3,
        siteId: getSiteWhiteLabelConfigId(),
        clientInfo
      })
    )
    .catch(() => {});
};

const notifySigninFailed = (email, clientInfo) =>
  notifyAuthFailure(ApiClient.signinFailed, "sign_in_failed", email, clientInfo);

// For the forgot-password page, on every reset request: Cognito answers the
// same whether or not the email has an account here, so the backend decides.
// On stock Kubera it emails a WL-only client a link to their portal's reset
// page; everywhere else it does nothing.
export const notifyResetPasswordRequested = email => {
  const { name, version } = detect() || {};
  const clientInfo = JSON.stringify({
    browser: { name, version },
    isMobile: isMobile(),
    os: getOsDetails()
  });
  notifyAuthFailure(ApiClient.resetPasswordRequested, "forgot_password_requested", email, clientInfo);
};

export const signInWithEmailPassword = (email, password, onSuccess, onError, parentAction = parentActions.SIGNIN) => {
  const { name, version } = detect() || {};
  const isMobileDevice = isMobile();
  const osDetails = getOsDetails();
  const getRecaptchaTokenAndSignIn = ipInfo => {
    getRecaptchaToken("sign_in")
      .then(token => {
        const getToken = () => {
          const params = getQueryParams(window.location);

          if (params?.test !== "true") {
            return token;
          }

          return isV2WidgetLoaded ? token : token + 123;
        };
        const clientInfo = JSON.stringify({
          browser: { name, version },
          isMobile: isMobileDevice,
          os: osDetails,
          ipInfo: ipInfo && ipInfo.payload
        });
        Auth.signIn(email, password, {
          parentAction: parentAction,
          recaptchaToken: getToken(),
          type: getRecaptchaTokenType(),
          clientInfo
        })
          .then(data => {
            onSuccess(data);
          })
          .catch(error => {
            if (error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
              if (parentAction === parentActions.SIGNUP) {
                onError(error, true);
                return;
              }
              error.message = "Something’s wrong. Please try again after some time.";
            }
            if (parentAction === parentActions.SIGNIN && isInvalidCredentialsError(error)) {
              notifySigninFailed(email, clientInfo);
            }
            resetRecaptchaV2();
            onError(error);
          });
      })
      .catch(error => {
        if (parentAction === parentActions.SIGNUP && error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
          onError(error, true);
          return;
        }
        onError(error);
      });
  };
  ApiClient.getIpInfo(getUuid())
    .then(ipInfo => {
      getRecaptchaTokenAndSignIn(ipInfo);
    })
    .catch(() => {
      getRecaptchaTokenAndSignIn();
    });
};

const toAuthError = apiError => {
  if (apiError?.code) {
    return apiError;
  }
  return {
    code: apiError?.errorCode,
    message: apiError?.errorMessage || i18n.t("somethingWentWrong")
  };
};

const SIGNUP_FAILED = "SIGNUP_FAILED";
const signupSteps = { SIGN_UP: "sign_up", VERIFY: "verify" };
const signupFailureSources = { TOKEN: "token", SERVER: "server", NETWORK: "network", CLIENT: "client" };

const SIGNUP_FAILURE_TAG = "signup_failure";
const SIGNUP_FAILURE_TAG_VALUE = "SignupFailure";
const NO_FAILURE_CODE = "none";
const SERVICE_UNAVAILABLE_STATUS = 503;

const expectedSignupErrorCodes = {
  [signupSteps.SIGN_UP]: [
    cognitoErrorCodes.USERNAME_EXISTS,
    cognitoErrorCodes.INVALID_PASSWORD,
    cognitoErrorCodes.USER_NOT_CONFIRMED,
    cognitoErrorCodes.NOT_AUTHORIZED
  ],
  [signupSteps.VERIFY]: [cognitoErrorCodes.CODE_MISMATCH, cognitoErrorCodes.EXPIRED_CODE]
};

const signupPageId = getUuid();

const isUncheckedRecaptchaV2Error = error => isV2WidgetLoaded && error?.code === USER_LAMBDA_VALIDATION_EXCEPTION;

const getSignupFailureCode = error =>
  error?.code === apiErrorCodes.UNKNOWN_ERROR && error?.httpStatus
    ? `http_${error.httpStatus}`
    : String(error?.code ?? NO_FAILURE_CODE);

const captureSignupFailure = (step, source, email, error, details = {}) => {
  Sentry.withScope(scope => {
    scope.setLevel("warning");
    scope.setFingerprint([SIGNUP_FAILED, step, source, getSignupFailureCode(error)]);
    scope.setTag(SIGNUP_FAILURE_TAG, SIGNUP_FAILURE_TAG_VALUE);
    scope.setTags({
      step,
      source,
      recaptchaWidget: getRecaptchaTokenType(),
      emailDomain: email?.split("@")[1],
      test: getQueryParams(window.location)?.test === "true",
      pageId: signupPageId
    });
    scope.setExtras({
      errorCode: error?.code,
      httpStatus: error?.httpStatus,
      errorMessage: typeof error === "string" ? error : error?.message ?? error?.code ?? String(error),
      ...details
    });
    Sentry.captureMessage(SIGNUP_FAILED);
  });
};

const reportSignupPayloadError = (step, email, payloadError) => {
  if (expectedSignupErrorCodes[step].includes(payloadError?.code)) {
    return;
  }
  captureSignupFailure(step, signupFailureSources.SERVER, email, payloadError);
};

const reportSignupRequestFailure = (step, email, error, startedAt) => {
  if (!(error instanceof ApiError)) {
    captureSignupFailure(step, signupFailureSources.CLIENT, email, error);
    return;
  }
  if (error.httpStatus === SERVICE_UNAVAILABLE_STATUS || error.errorCode === apiErrorCodes.CLIENT_BREAKING_CHANGES) {
    return;
  }
  if (error.errorCode === apiErrorCodes.NO_RESPONSE) {
    captureSignupFailure(
      step,
      signupFailureSources.NETWORK,
      email,
      { code: error.errorCode, message: error.errorMessage },
      { online: navigator.onLine, elapsedMs: Date.now() - startedAt }
    );
    return;
  }
  captureSignupFailure(step, signupFailureSources.SERVER, email, {
    code: error.errorCode,
    message: error.errorMessage,
    httpStatus: error.httpStatus
  });
};

const allowSignupPlanB = true;
export const signUpWithEmailPassword = (name, email, password, invitationId = null, onSuccess, onError) => {
  getRecaptchaToken("sign_up")
    .then(token => {
      const getToken = () => {
        const params = getQueryParams(window.location);

        if (params?.test !== "true") {
          return token;
        }

        return isV2WidgetLoaded ? token : token + 123;
      };
      const handleError = error => {
        resetRecaptchaV2();
        if (error.code === cognitoErrorCodes.NOT_AUTHORIZED) {
          error.message = "Sign Up temporarily paused. We'll open up soon";
        } else if (error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
          error.message = "Something’s wrong. Please try again after some time.";
        }
        onError(error);
      };
      if (allowSignupPlanB) {
        const startedAt = Date.now();
        ApiClient.signUp(getUuid(), {
          email,
          name,
          password,
          recaptchaToken: getToken(),
          type: getRecaptchaTokenType()
        })
          .then(({ payload }) => {
            console.log("payload", payload);
            if (payload.error === null) {
              onSuccess(payload);
            } else {
              reportSignupPayloadError(signupSteps.SIGN_UP, email, payload.error);
              handleError(payload.error);
            }
          })
          .catch(error => {
            reportSignupRequestFailure(signupSteps.SIGN_UP, email, error, startedAt);
            handleError(toAuthError(error));
          });
        return;
      }

      var attributes = {
        email: email,
        name: name
      };
      if (invitationId) {
        attributes["custom:inv_token"] = invitationId;
      }

      Auth.signUp({
        username: email,
        password: password,
        attributes: attributes,
        clientMetadata: {
          parentAction: parentActions.SIGNUP,
          recaptchaToken: token,
          type: getRecaptchaTokenType()
        }
      })
        .then(data => {
          onSuccess(data);
        })
        .catch(error => {
          handleError(error);
        });
    })
    .catch(error => {
      if (!isUncheckedRecaptchaV2Error(error)) {
        captureSignupFailure(signupSteps.SIGN_UP, signupFailureSources.TOKEN, email, error);
      }
      onError(error);
    });
};

export const verifySignInWithEmailPassword = async (password, onSuccess, onError) => {
  const isPasswordCorrect = await verifySigninPassword(password).catch(error => {
    if (error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
      error.message = "Something’s wrong. Please try again after some time.";
    }
    console.log("error", error);
    onError(error);
  });
  if (isPasswordCorrect === true) {
    onSuccess();
    return;
  }
};

export const confirmSignin = async (user, code, mfaType) => {
  const loggedUser = await Auth.confirmSignIn(
    user, // Return object from Auth.signIn()
    code, // Confirmation code
    mfaType, // MFA Type e.g. SMS, TOTP
    {
      parentAction: parentActions.SIGNIN
    }
  ).catch(err => {
    console.warn(err);
  });

  return loggedUser;
};

export const confirmSignupPlanB = async (
  { name, email, password, verificationCode, invitationId = "" },
  onSuccess,
  onError
) => {
  getRecaptchaToken("sign_up")
    .then(recaptchaToken => {
      const startedAt = Date.now();
      ApiClient.signupVerify(getUuid(), {
        name,
        email,
        password,
        verificationCode,
        ...(invitationId && { invitationId }),
        recaptchaToken,
        type: getRecaptchaTokenType()
      })
        .then(data => {
          if (data.payload.error === null) {
            onSuccess(data);
          } else {
            reportSignupPayloadError(signupSteps.VERIFY, email, data.payload.error);
            resetRecaptchaV2();
            onError(data.payload.error);
          }
        })
        .catch(error => {
          reportSignupRequestFailure(signupSteps.VERIFY, email, error, startedAt);
          resetRecaptchaV2();
          onError(toAuthError(error));
        });
    })
    .catch(error => {
      if (!isUncheckedRecaptchaV2Error(error)) {
        captureSignupFailure(signupSteps.VERIFY, signupFailureSources.TOKEN, email, error);
      }
      onError(error);
    });
};

export const confirmSignup = async (email, code, onSuccess, onError) => {
  await Auth.confirmSignUp(email, code, {
    forceAliasCreation: true
  })
    .then(data => {
      onSuccess(data);
    })
    .catch(error => {
      onError(error);
    });
};

export const resendSignupCode = async (email, onSuccess, onError) => {
  await Auth.resendSignUp(email)
    .then(data => {
      onSuccess(data);
    })
    .catch(error => {
      onError(error);
    });
};

export const forgotPassword = async (
  email,
  onSuccess,
  onError,
  flag = {
    requestSessionId: false
  }
) => {
  let uniqueSessionId = null;
  if (flag.requestSessionId) {
    uniqueSessionId = await getUniqueSessionId().catch(err => {
      uniqueSessionId = null;
      console.warn(err);
    });

    if (!uniqueSessionId) {
      const error = new Error("Something’s wrong. Please try again after some time.");
      onError(error);
      return;
    }
  }
  getRecaptchaToken("forgot_password")
    .then(token => {
      Auth.forgotPassword(email, { recaptchaToken: token, uniqueSessionId, type: getRecaptchaTokenType() })
        .then(data => {
          onSuccess(data);
        })
        .catch(error => {
          if (error.code === USER_LAMBDA_VALIDATION_EXCEPTION) {
            error.message = "Something’s wrong. Please try again after some time.";
          }
          resetRecaptchaV2();
          onError(error);
        });
    })
    .catch(error => {
      onError(error);
    });
};

export const forgotPasswordSubmit = async (email, code, newPassword, onSuccess, onError, clientMetadata) => {
  let isError = false;
  const data = await Auth.forgotPasswordSubmit(email, code, newPassword, clientMetadata).catch(error => {
    isError = true;
    onError(error);
  });

  if (!isError && clientMetadata?.parentAction === parentActions.SET_PASSWORD) {
    await store.dispatch(userSetPasswordUpdated());
  }
  if (!isError) {
    onSuccess(data);
  }
};

export const changePassword = async (currentPassword, newPassword, onSuccess, onError) => {
  await Auth.changePassword(undefined, currentPassword, newPassword)
    .then(data => {
      onSuccess();
    })
    .catch(error => onError(error));
};

export const createPassword = async (user, newPassword, onSuccess, onError) => {
  await Auth.completeNewPassword(user, newPassword, {}, { parentAction: parentActions.NEW_PASSWORD })
    .then(data => {
      onSuccess();
    })
    .catch(error => onError(error));
};

export const passwordStrengthLevels = {
  WEAK: { color: "rgba(255, 2, 0)" },
  GOOD: { color: "rgba(255, 185, 3)" },
  STRONG: { color: "rgba(2, 185, 86)" }
};

export const getPasswordStrength = pass => {
  if (!pass === true) {
    return passwordStrengthLevels.WEAK;
  }
  var score = 0;

  // award every unique letter until 5 repetitions
  var letters = {};
  for (var i = 0; i < pass.length; i++) {
    letters[pass[i]] = (letters[pass[i]] || 0) + 1;
    score += 5.0 / letters[pass[i]];
  }

  // bonus points for mixing it up
  var variations = {
    digits: /\d/.test(pass),
    lower: /[a-z]/.test(pass),
    upper: /[A-Z]/.test(pass),
    nonWords: /\W/.test(pass)
  };

  let variationCount = 0;
  for (var check in variations) {
    variationCount += variations[check] === true ? 1 : 0;
  }
  score += (variationCount - 1) * 10;

  score = parseInt(score);

  if (score > 80) {
    return passwordStrengthLevels.STRONG;
  } else if (score > 60) {
    return passwordStrengthLevels.GOOD;
  } else {
    return passwordStrengthLevels.WEAK;
  }
};

export const configureAmplify = state => {
  const userPoolId = siteConfigUserPoolIdSelector(state);
  const clientId = siteConfigClientIdSelector(state);

  if (isAppInWhiteLabelMode() === true) {
    const config = {
      Auth: {
        Cognito: {
          userPoolId: userPoolId,
          userPoolClientId: clientId
        }
      }
    };
    try {
      console.log("Amplify config", config);
      Amplify.configure(config);
    } catch (e) {
      console.log(e);
    }
  } else {
    installAwsWafTokenForCognito();
    const signInCallback = process.env.REACT_APP_COGNITO_BASE_URL + process.env.REACT_APP_COGNITO_SIGNIN_CALLBACK_PATH;
    const signOutCallback =
      process.env.REACT_APP_COGNITO_BASE_URL + process.env.REACT_APP_COGNITO_SIGNOUT_CALLBACK_PATH;

    const config = {
      Auth: {
        Cognito: {
          userPoolId: userPoolId,
          userPoolClientId: clientId,

          loginWith: {
            oauth: {
              domain: process.env.REACT_APP_COGNITO_DOMAIN,
              scopes: ["email", "openid", "profile"],
              redirectSignIn: [signInCallback],
              redirectSignOut: [signOutCallback],
              responseType: "code"
            }
          }
        }
      }
    };
    if (
      [Routes.LINK_PROVIDER_PAGE, Routes.REDIRECT_CALLBACK, Routes.STRIPE_CALLBACK].includes(window.location.pathname)
    ) {
      delete config.Auth.Cognito.loginWith.oauth.redirectSignIn;
    }
    try {
      Amplify.configure(config);
    } catch (e) {
      console.log(e);
    }
  }
};

export const updateCurrentUserAttributes = async (attributes, onSuccess, onError) => {
  await Auth.updateUserAttributes(undefined, attributes)
    .then(() => onSuccess())
    .catch(err => onError(err));
};

export const verifyPhone = async phone => {
  let formattedPhoneNumber = formatPhoneNumber(phone);
  await Auth.updateUserAttributes(undefined, {
    phone_number: formattedPhoneNumber
  });

  return new Promise((resolve, reject) => {
    Auth.verifyCurrentUserAttribute("phone_number")
      .then(() => {
        resolve(phone);
      })
      .catch(err => {
        reject(err.message);
      });
  });
};

export const verifyPhoneCode = async code => {
  const response = await Auth.verifyCurrentUserAttributeSubmit("phone_number", code);

  return response;
};

export const setPreferredMFA = async preference => {
  const setPref = await Auth.setPreferredMFA(preference).catch(err => {
    console.warn(err);
  });

  return setPref;
};

export const getQRStr = async () => {
  const qrStr = await Auth.setupTOTP();

  return qrStr;
};

export const verifyTotpToken = async verifyCode => {
  const verifyState = await Auth.verifyTotpToken(verifyCode).catch(err => {
    console.warn(err);
  });

  return verifyState;
};

export const setTokenForViewMode = () => {
  if (isAppInViewMode() === false) {
    return;
  }
  const params = getQueryParams(window.location);
  if (!params === true) {
    return;
  }
  const token = params.token || params.t;

  if (!token === false) {
    // Clear stored passcode if different token is being loaded
    const storedToken = sessionStorage.getItem(VIEW_ONLY_TOKEN_KEY);
    if (!storedToken === false && storedToken !== token) {
      setPasscodeForViewMode(null);
      setUserNameForViewMode(null);
    }

    sessionStorage.setItem(VIEW_ONLY_TOKEN_KEY, token);
  }
};

export const setPasscodeForViewMode = passcode => {
  if (isAppInViewMode() === false) {
    return;
  }
  if (!passcode === true) {
    sessionStorage.removeItem(VIEW_ONLY_PASSCODE_KEY);
  } else {
    sessionStorage.setItem(VIEW_ONLY_PASSCODE_KEY, passcode);
  }
};

export const getUserNameForViewMode = () => {
  if (isAppInViewMode() === false) {
    return null;
  }
  return sessionStorage.getItem(VIEW_ONLY_USER_NAME);
};

const eventMethods = { signOut };

eventBus.addEventListener("any", event => {
  const { eventName, data } = event.detail;
  if (eventMethods[eventName]) {
    eventMethods[eventName].apply(this, data);
  }
});
